// ═══════════════════════════════════════════════════════════════════════════
// credits.js — credit engine (client-side API).
//
// All balance mutations go through SECURITY DEFINER RPC functions in
// supabase-migrations/018_pricing_credits_system.sql. The client never
// writes to user_credits or credit_transactions directly.
//
// Primary API (use in new code):
//   getBalance(userId)                        → { balance, topup_balance, total }
//   canAfford(userId, amount)                 → boolean
//   deductCredits(userId, amount, action, md) → { balance, topup_balance, total }
//   refundCredits(userId, amount, action, …)  → { balance, total }
//   getRecentTransactions(userId, limit)      → array
//   getUserCredits(userId)                    → full user_credits row
//   grantDailyOverflow(userId)                → { granted, reason }
//   canAffordAction(userId, action)           → boolean (uses CREDIT_COSTS)
//   deductForAction(userId, action, md)       → deduct using CREDIT_COSTS map
//
// Legacy shims (kept so existing callers keep working during Stage 5 rollout):
//   consumeCredits(userId, amount)
//   renewCreditsIfDue(userId)   — no-op now; refills are Stripe-driven
//   isCreditLimitError(err)
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from './supabase';
import { CREDIT_COSTS, costOf } from './creditCosts';
import { logAdminAction } from './audit';

function assertReady(userId) {
  if (!supabase) throw new Error('Supabase not initialized');
  if (!userId) throw new Error('Not authenticated');
}

/**
 * Mini-spec #59 — admin grant. The caller must be is_admin = true on
 * their podcaster_profiles row (enforced inside the SECURITY DEFINER
 * RPC). Returns the recipient's new topup balance + total.
 */
export async function grantCredits(targetUserId, amount, note = 'admin grant') {
  if (!supabase) throw new Error('Supabase not initialized');
  if (!targetUserId) throw new Error('targetUserId required');
  if (!amount || amount <= 0) throw new Error('amount must be > 0');
  const { data, error } = await supabase.rpc('admin_grant_credits', {
    p_user_id: targetUserId,
    p_amount:  amount,
    p_note:    note,
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  logAdminAction('grant_credits', { targetType: 'user', targetId: targetUserId, detail: { amount, note } });
  return data;
}

/**
 * Mini-spec #60 — admin set exact topup. Use to take credits AWAY
 * (or zero them out) — pass any non-negative integer. RPC enforces
 * is_admin server-side.
 */
export async function setCreditsExact(targetUserId, topup, note = 'admin set') {
  if (!supabase) throw new Error('Supabase not initialized');
  if (!targetUserId) throw new Error('targetUserId required');
  if (topup < 0) throw new Error('topup must be ≥ 0');
  const { data, error } = await supabase.rpc('admin_set_credits', {
    p_user_id: targetUserId,
    p_topup:   topup,
    p_note:    note,
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  logAdminAction('set_credits', { targetType: 'user', targetId: targetUserId, detail: { topup, note } });
  return data;
}

/**
 * Mini-spec #60 — admin patch a user profile from the user-detail
 * modal. Whitelisted server-side to: full_name, plan, niche,
 * marketplace_visible, is_admin, unlimited_credits, verified,
 * professional_title, bio. Pass a partial object.
 */
export async function updateUserAdmin(targetUserId, patch) {
  if (!supabase) throw new Error('Supabase not initialized');
  if (!targetUserId) throw new Error('targetUserId required');
  if (!patch || typeof patch !== 'object') throw new Error('patch required');
  const { data, error } = await supabase.rpc('admin_update_user', {
    p_user_id: targetUserId,
    p_patch:   patch,
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  logAdminAction('update_user', { targetType: 'user', targetId: targetUserId, detail: patch });
  return data;
}

// ── Primary API ────────────────────────────────────────────────────────────

/**
 * Return { balance, topup_balance, total } for a user.
 *
 * Triggers the server-side refill engine first (mini-spec #5) so Free
 * users always see their fresh daily allotment and paid users get the
 * bonus when they hit zero. Pass { skipRefill: true } from internal
 * callers (like canAfford) that already ran the refill themselves.
 */
export async function getBalance(userId, opts = {}) {
  assertReady(userId);
  if (!opts.skipRefill) {
    await applyAutomaticRefills(userId);
  }
  const { data, error } = await supabase
    .from('user_credits')
    .select('balance, topup_balance')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) { console.error('getBalance:', error); throw error; }
  const balance       = data?.balance ?? 0;
  const topup_balance = data?.topup_balance ?? 0;
  return { balance, topup_balance, total: balance + topup_balance };
}

/** Return the full user_credits row (balance, allotment, rollover_cap, refill date).
 *  Soft-fails (returns null) when Supabase isn't configured so display
 *  components can render their tier defaults rather than crash on dev. */
export async function getUserCredits(userId) {
  if (!supabase || !userId) return null;
  const { data, error } = await supabase
    .from('user_credits')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) { console.error('getUserCredits:', error); return null; }
  return data || null;
}

/**
 * Mini-spec #60: unlimited-credits override. When a profile has
 * unlimited_credits = true (set for drew@smileyhousemedia.com in
 * migration 035 + any future internal team), every credit gate
 * returns "yes" and every deduct call short-circuits to a no-op
 * success. Cached for 60s so we don't roundtrip on every call.
 */
const UNLIMITED_TTL_MS = 60_000;
const unlimitedCache = new Map(); // userId → { lastCheckedAt, value }

async function isUnlimited(userId) {
  if (!supabase || !userId) return false;
  const c = unlimitedCache.get(userId);
  if (c && Date.now() - c.lastCheckedAt < UNLIMITED_TTL_MS) return c.value;
  try {
    const { data } = await supabase
      .from('podcaster_profiles')
      .select('unlimited_credits')
      .eq('user_id', userId)
      .maybeSingle();
    const value = !!data?.unlimited_credits;
    unlimitedCache.set(userId, { lastCheckedAt: Date.now(), value });
    return value;
  } catch { return false; }
}

export async function canAfford(userId, amount) {
  if (amount <= 0) return true;
  if (await isUnlimited(userId)) return true;
  await applyAutomaticRefills(userId);
  const { total } = await getBalance(userId, { skipRefill: true });
  return total >= amount;
}

export async function canAffordAction(userId, action) {
  return canAfford(userId, costOf(action));
}

/**
 * Run the server-side daily refill / daily bonus logic (mini-spec #5).
 *
 * Free users: replaces balance with their daily allotment (3) at most
 * once per UTC day. Paid users: tops up the dailyBonus amount when
 * subscription balance has hit zero, at most once per UTC day.
 *
 * Throttled per browser session — once per minute is plenty since the
 * server itself enforces "once per day" via the date columns. The
 * throttle just keeps us from hammering the RPC on every page nav.
 */
const REFILL_TTL_MS = 60_000;
const refillCache = new Map(); // userId → { lastRunAt, lastResult }

export async function applyAutomaticRefills(userId, opts = {}) {
  if (!supabase || !userId) return null;
  const force = !!opts.force;
  const cached = refillCache.get(userId);
  if (!force && cached && (Date.now() - cached.lastRunAt) < REFILL_TTL_MS) {
    return cached.lastResult;
  }
  try {
    const { data, error } = await supabase.rpc('apply_automatic_refills', { p_user_id: userId });
    if (error) {
      // Don't throw — refills are a nice-to-have. Worst case: user sees
      // a stale balance until next call. Logging it for observability.
      console.warn('applyAutomaticRefills:', error.message);
      return null;
    }
    refillCache.set(userId, { lastRunAt: Date.now(), lastResult: data });
    return data; // { applied, kind, balance, tier, reason }
  } catch (err) {
    console.warn('applyAutomaticRefills failed:', err?.message || err);
    return null;
  }
}

/**
 * Deduct credits atomically. Drains monthly balance first, then topup_balance.
 * Throws with code 'insufficient_credits' (matchable via isInsufficientError)
 * when the user can't cover the cost.
 */
export async function deductCredits(userId, amount, action, metadata = {}) {
  assertReady(userId);
  if (!action) throw new Error('deductCredits: action is required');
  // Mini-spec #60: short-circuit deduction for unlimited accounts.
  // Returns a sane fake "balance" so callers that read .total don't NaN.
  if (await isUnlimited(userId)) {
    return { balance: 999999, topup_balance: 0, total: 999999, unlimited: true };
  }
  const { data, error } = await supabase.rpc('deduct_credits', {
    p_user_id:  userId,
    p_amount:   amount,
    p_action:   action,
    p_metadata: metadata,
  });
  if (error) { console.error('deductCredits:', error); throw error; }
  return data; // { balance, topup_balance, total }
}

/** Deduct the canonical cost for a registered action (preferred over deductCredits). */
export async function deductForAction(userId, action, metadata = {}) {
  const amount = costOf(action);
  if (amount <= 0) return { balance: 0, topup_balance: 0, total: 0 };
  return deductCredits(userId, amount, action, metadata);
}

/**
 * Refund credits for a failed generation. The refund goes back into the
 * monthly balance bucket and is not capped against rollover_cap.
 */
export async function refundCredits(userId, amount, action, opts = {}) {
  assertReady(userId);
  const { data, error } = await supabase.rpc('refund_credits', {
    p_user_id:        userId,
    p_amount:         amount,
    p_action:         action || 'refund',
    p_original_tx_id: opts.originalTxId || null,
    p_reason:         opts.reason || null,
  });
  if (error) { console.error('refundCredits:', error); throw error; }
  return data; // { balance, total }
}

/** Grant daily overflow credits if eligible (balance at 0, tier grants overflow,
 *  once per calendar day). Safe to call on app load. */
export async function grantDailyOverflow(userId) {
  assertReady(userId);
  const { data, error } = await supabase.rpc('grant_daily_overflow', { p_user_id: userId });
  if (error) { console.error('grantDailyOverflow:', error); return { granted: 0, reason: 'error' }; }
  return data;
}

/** Last N credit_transactions for the user, newest first. */
export async function getRecentTransactions(userId, limit = 10) {
  assertReady(userId);
  const { data, error } = await supabase
    .from('credit_transactions')
    .select('id, amount, balance_after, type, action, metadata, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) { console.error('getRecentTransactions:', error); throw error; }
  return data || [];
}

// ── Legacy shims ───────────────────────────────────────────────────────────

/**
 * Legacy entry point. Existing callers pass an integer amount they already
 * computed. New code should use deductForAction(userId, 'episode_build_simple').
 */
export async function consumeCredits(userId, amount = CREDIT_COSTS.episode_build_simple) {
  const intAmount = Math.max(1, Math.ceil(Number(amount) || 0));
  return deductCredits(userId, intAmount, 'legacy_consume', { legacy_amount: amount });
}

/**
 * Legacy entry point. Refills are now Stripe-webhook-driven, so this is a
 * no-op in normal operation. Returns the current user_credits row so
 * callers that relied on the old `renew_credits_if_due` returning a
 * profile-like object don't crash.
 */
export async function renewCreditsIfDue(userId) {
  if (!supabase || !userId) return null;
  try { return await getUserCredits(userId); }
  catch { return null; }
}

export function isCreditLimitError(err) {
  const msg = err?.message || '';
  return /insufficient_credits|Credit limit reached/i.test(msg);
}

// New, more specific name — prefer in new code.
export const isInsufficientCreditsError = isCreditLimitError;
